import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { getDetails } from 'use-places-autocomplete';
import {
  hasStreetNumberComponent,
  mapAutocompleteAddress,
  resolveCity,
  resolvePostalCode,
  useAddressAutocomplete,
} from './helpers';
import {
  type HouseNumberRecoveryFailure,
  recoverHouseNumber,
  recoverPostalCode,
  suggestionHouseNumber,
} from './recover-house-number';
import { Image } from '@/components/image';
import { Combobox } from '@/components/ui/form/combobox';
import { useStringsOverride } from '@/redux/cmsConfig';
import { tryCatch } from '@/utils/global';
import { type GoogleAddressComponentFields } from '@/types/fulfillment';

export interface GoogleAddress {
  street_address: string;
  street_address_2: string;
  city: string;
  state: string;
  zip_code: string;
}

/**
 * `no_house_number` — the suggestion text itself has no house number, so there is nothing to
 * recover. `unconfirmed` — it has one, but geocoding the suggestion did not confirm a matching
 * numbered address (TEAM-15253). `suggestion` is the picked suggestion's text, e.g. "2520 El Camino
 * Real, Carlsbad, CA, USA".
 */
export type MissingStreetNumberDetails =
  | { reason: 'no_house_number'; suggestion: string }
  | { reason: 'unconfirmed'; recovery: HouseNumberRecoveryFailure; suggestion: string };

/**
 * Why a pick that already fired `onChange` will never reach `onUpdate` or `onMissingStreetNumber`:
 * the widget unmounted while the pick was still resolving, or Google's place-details lookup failed
 * (rejected, or came back with no address components).
 */
export type SelectionAbandonedReason = 'unmounted' | 'details_failed';

export interface StreetNumberRecoveredDetails {
  /** The road-level address the picked place resolved to before recovery. */
  partialAddress: GoogleAddress;
  suggestion: string;
}

export interface PostalCodeRecoveredDetails {
  /** The numbered address the picked place resolved to, without a ZIP. */
  partialAddress: GoogleAddress;
  suggestion: string;
}

/**
 * Google may omit any of these components for a given selection. Every field is normalized so a
 * missing component becomes '' rather than the literal string "undefined": city via resolveCity,
 * ZIP via resolvePostalCode, and street_address/state via nullish coalescing. A bare
 * String(postal_code) previously emitted "undefined", which Koala basket-validate rejected as
 * MalformedPayload on #/customer/zip (TEAM-14096); String(administrative_area_level_1) was the same
 * latent bug on #/customer/state. Consumers validate the resolved address before committing it so an
 * incomplete selection is caught up front.
 */
const toGoogleAddress = (components: GoogleAddressComponentFields): GoogleAddress => {
  const { administrative_area_level_1, street_number, route, subpremise } = components;

  return {
    street_address: [street_number, route].filter(Boolean).join(' ').trim(),
    street_address_2: subpremise ?? '',
    city: resolveCity(components),
    state: administrative_area_level_1 ?? '',
    zip_code: resolvePostalCode(components),
  };
};

interface Props {
  onChange?: () => void;
  onUpdate: (address: GoogleAddress) => void | Promise<void>;
  /**
   * Called (instead of onUpdate) when a required house number is missing from the selection —
   * TEAM-15047. Receives the resolved (incomplete) address so the consumer keeps the same telemetry
   * as onUpdate. Only fires when `requireStreetNumber` is set and the house number could not be
   * recovered (TEAM-15253).
   */
  onMissingStreetNumber?: (
    partialAddress: GoogleAddress,
    details: MissingStreetNumberDetails,
  ) => void;
  /**
   * Called just before onUpdate when a road-level pick had its typed house number recovered by
   * geocoding the suggestion (TEAM-15253). Telemetry only — the recovered address is committed
   * through onUpdate like any other pick.
   */
  onStreetNumberRecovered?: (
    recoveredAddress: GoogleAddress,
    details: StreetNumberRecoveredDetails,
  ) => void;
  /**
   * Called just before onUpdate when a numbered pick that came back without a ZIP had it recovered
   * by geocoding the suggestion (TEAM-15164). Telemetry only — the address is committed through
   * onUpdate. When the ZIP can't be recovered, onUpdate receives the pick without one instead.
   */
  onPostalCodeRecovered?: (
    recoveredAddress: GoogleAddress,
    details: PostalCodeRecoveredDetails,
  ) => void;
  /**
   * Called when a pick that already fired `onChange` is dropped without reaching `onUpdate` or
   * `onMissingStreetNumber`, so the consumer can undo what `onChange` started (TEAM-15253). Not
   * called for a pick superseded by a newer one — the newer pick carries on from its own onChange.
   */
  onSelectionAbandoned?: (reason: SelectionAbandonedReason) => void;
  /**
   * Restrict predictions to precise numbered street addresses and reject a selection whose
   * `street_number` component is missing; a numbered pick without a ZIP gets one geocode attempt to
   * recover it (TEAM-15164). Used by the delivery checkout.
   */
  requireStreetNumber?: boolean;
  hideLabel?: boolean;
}

export const GoogleAutocomplete = ({
  onChange,
  onUpdate,
  onMissingStreetNumber,
  onStreetNumberRecovered,
  onPostalCodeRecovered,
  onSelectionAbandoned,
  requireStreetNumber = false,
  hideLabel,
}: Props) => {
  // Increments on every pick so an earlier pick whose place details or geocode recovery resolve
  // late can't commit over (or report on) a newer one — and on unmount, so a pick still resolving
  // when the address editor closes is never committed.
  const latestSelection = useRef(0);
  // The pick that fired onChange but has not reached a consumer callback yet, if any.
  const pendingSelection = useRef<number | null>(null);
  const onSelectionAbandonedRef = useRef(onSelectionAbandoned);
  onSelectionAbandonedRef.current = onSelectionAbandoned;
  useEffect(
    () => () => {
      latestSelection.current += 1;
      if (pendingSelection.current !== null) {
        pendingSelection.current = null;
        onSelectionAbandonedRef.current?.('unmounted');
      }
    },
    [],
  );
  const { delivery_search_placeholder } = useStringsOverride('locations');
  const { clearSuggestions, data, ready, updateValue, googleMapFailed } = useAddressAutocomplete({
    restrictToStreetAddress: requireStreetNumber,
  });

  async function setSelection(suggestion: google.maps.places.AutocompletePrediction) {
    const selection = ++latestSelection.current;
    pendingSelection.current = selection;
    if (onChange) {
      onChange();
    }
    const { description, place_id } = suggestion;

    // When user selects a place, we can replace the keyword without
    // request data from API by setting the second parameter to `false`
    updateValue(description, false);
    clearSuggestions();

    // getDetails rejects with a bare status string on a non-OK status; catch it so the pick is
    // reported as abandoned instead of surfacing as an unhandled rejection.
    const [details] = await tryCatch(
      getDetails({
        placeId: place_id,
        fields: ['address_components', 'place_id', 'name', 'types'],
      }),
    );
    if (selection !== latestSelection.current) {
      return;
    }

    if (!details || typeof details === 'string' || !details.address_components) {
      pendingSelection.current = null;
      onSelectionAbandoned?.('details_failed');
      return;
    }

    const components = mapAutocompleteAddress(details.address_components);
    const address = toGoogleAddress(components);

    // TEAM-15047: when Google resolves the pick to a route with no `street_number` component the
    // house number is missing (the street name itself may start with a digit, e.g. "5th Avenue",
    // so we check the component, not the joined string). On the delivery checkout, never commit an
    // incomplete address that fails later at the delivery-quote step with a generic error.
    if (requireStreetNumber && !hasStreetNumberComponent(components)) {
      if (!suggestionHouseNumber(suggestion)) {
        pendingSelection.current = null;
        onMissingStreetNumber?.(address, { reason: 'no_house_number', suggestion: description });
        return;
      }

      // TEAM-15253: the diner typed a house number but Google only knows the road. Geocode the
      // suggestion's own text and commit the result only if it is the same numbered address.
      const recovery = await recoverHouseNumber(suggestion);
      if (selection !== latestSelection.current) {
        return;
      }

      pendingSelection.current = null;
      if (!recovery.recovered) {
        onMissingStreetNumber?.(address, {
          reason: 'unconfirmed',
          recovery: recovery.outcome,
          suggestion: description,
        });
        return;
      }

      const recoveredAddress = toGoogleAddress(mapAutocompleteAddress(recovery.components));
      onStreetNumberRecovered?.(recoveredAddress, {
        partialAddress: address,
        suggestion: description,
      });
      void onUpdate(recoveredAddress);
      return;
    }

    // TEAM-15164: a numbered pick can still come back without a ZIP ("424 Hahlo St, Houston, TX").
    // Geocode the suggestion and take only the ZIP from a result that is the same address; if none
    // is confirmed, commit the pick as-is and let the consumer ask the diner for the ZIP.
    if (requireStreetNumber && !address.zip_code) {
      const recovery = await recoverPostalCode(suggestion);
      if (selection !== latestSelection.current) {
        return;
      }

      if (recovery.recovered) {
        const recoveredAddress = { ...address, zip_code: recovery.postalCode };
        pendingSelection.current = null;
        onPostalCodeRecovered?.(recoveredAddress, {
          partialAddress: address,
          suggestion: description,
        });
        void onUpdate(recoveredAddress);
        return;
      }
    }

    pendingSelection.current = null;
    void onUpdate(address);
  }

  if (!ready) {
    return null;
  }

  if (googleMapFailed) {
    return (
      <div>
        <p>Please try refreshing the page. There was an error loading the Google Maps API.</p>
      </div>
    );
  }

  return (
    <Combobox
      label={hideLabel ? '' : delivery_search_placeholder}
      items={data.map((suggestion) => {
        const {
          structured_formatting: { main_text, secondary_text },
        } = suggestion;

        return {
          value: suggestion,
          label: `${main_text}, ${secondary_text}`,
        };
      })}
      onChange={async (value) => {
        // @ts-expect-error: value is unknown but we know it equates to the `value` property in the object returned from `data.map` above
        await setSelection(value);
      }}
      onUpdate={(value) => {
        updateValue(value);
      }}
    >
      <PoweredByGoogle>
        <Image
          src="/static/img/powered_by_google_on_white_hdpi.png"
          alt="Powered By Google"
          height={12}
          width={96}
        />
      </PoweredByGoogle>
    </Combobox>
  );
};

const PoweredByGoogle = styled.div({
  marginInlineStart: 'auto',
  marginInlineEnd: '1rem',
  marginBlockEnd: '.05rem',
});
